import React, { useCallback } from "react";
import { CircularProgress } from "@mui/material";
import {
    SovosBox,
    SovosButton,
    SovosDialog,
    SovosDialogActions,
    SovosDialogContent,
    SovosDialogTitle,
    SovosIconButton,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, Close } from "mosaic-react-icons";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { AI_CONFIDENCE_GRADIENT, KPI_BORDER_COLORS } from "../constants";
import { resolveAiTargetToGridFieldId, stripCdmPrefix } from "../utils";
import MappingDropdown from "../components/MappingDropdown";
import MappingGridToolbar from "../components/MappingGridToolbar";
import StatTile from "../components/StatTile";

const StepMapColumns = ({
    needsMappingCount,
    readyCount,
    skippedCount,
    totalColumns,
    mappings,
    mschFields,
    usedFields,
    aiSuggestedColumns,
    aiAppliedSkippedCsv,
    aiMappingResult,
    showAiMappingDialog,
    originalMappingsRef,
    hideDoNotImport,
    showDoNotImportToggle,
    dgItems,
    dgHeader,
    dgAvailableFilters,
    i18n,
    loading,
    onMappingChange,
    onRevert,
    onDismissAiBanner,
    onApplyAiMappings,
    onCloseAiDialog,
    onToggleHideDoNotImport,
}) => {
    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const value = mappings[csvColumn] ?? null;
            const isAiSuggested = aiSuggestedColumns.has(csvColumn);

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <SovosTypography variant="caption" fontWeight={600} color="text.primary" noWrap>
                            {displayName}
                        </SovosTypography>
                        {isAiSuggested && (
                            <AutoAwesome sx={{ fontSize: 11, color: "primary.main" }} titleAccess="Applied by AI" />
                        )}
                    </SovosBox>
                    <MappingDropdown
                        csvColumn={csvColumn}
                        displayName={displayName}
                        value={value}
                        originalValue={originalMappingsRef.current[csvColumn] ?? null}
                        mschFields={mschFields}
                        usedFields={usedFields}
                        onChange={onMappingChange}
                        onRevert={onRevert}
                        i18n={i18n}
                    />
                </SovosBox>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mappings, usedFields, mschFields, aiSuggestedColumns, onMappingChange, onRevert]
    );

    if (loading) {
        return (
            <SovosBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    minHeight: "calc(100vh - 280px)",
                }}
            >
                <CircularProgress size={28} />
                <SovosTypography variant="body2" sx={{ color: "text.secondary" }}>
                    Loading mapping data…
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", gap: 1.5, mb: aiSuggestedColumns.size > 0 ? 1.5 : 3 }}>
                <StatTile label="Columns need to be mapped" value={needsMappingCount} borderColor={KPI_BORDER_COLORS[0]} />
                <StatTile label="Ready for next step" value={readyCount} borderColor={KPI_BORDER_COLORS[1]} />
                <StatTile label="Columns skipped mapping" value={skippedCount} borderColor={KPI_BORDER_COLORS[2]} />
                <StatTile label="All columns" value={totalColumns} borderColor={KPI_BORDER_COLORS[3]} />
            </SovosBox>

            {aiSuggestedColumns.size > 0 && (
                <SovosBox sx={{ mb: 3, border: "1px solid", borderColor: "primary.light", borderRadius: 1, overflow: "hidden" }}>
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            bgcolor: "primary.50",
                            borderBottom: "1px solid",
                            borderColor: "primary.light",
                        }}
                    >
                        <AutoAwesome sx={{ fontSize: 14, color: "primary.main" }} />
                        <SovosTypography variant="caption" fontWeight={600} color="primary.main" sx={{ flex: 1 }}>
                            {`Sovi AI has analyzed your columns and suggested ${aiSuggestedColumns.size} field mapping${aiSuggestedColumns.size !== 1 ? "s" : ""} with high confidence scores (>80%). You can review these suggestions and manually adjust any mapping as needed.`}
                        </SovosTypography>
                        <SovosIconButton size="small" tooltipText="Dismiss" onClick={onDismissAiBanner}>
                            <Close sx={{ fontSize: 14 }} />
                        </SovosIconButton>
                    </SovosBox>
                    {aiAppliedSkippedCsv > 0 && (
                        <SovosBox sx={{ px: 1.5, py: 0.75, bgcolor: "warning.light", borderBottom: "1px solid", borderColor: "warning.light" }}>
                            <SovosTypography variant="caption" color="warning.dark">
                                {`${aiAppliedSkippedCsv} proposed row(s) were not applied: the CSV column name did not match any column in this file.`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    <SovosBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 0.5,
                            bgcolor: "action.selected",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>CSV column</SovosTypography>
                        <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                        <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1 }}>Map to</SovosTypography>
                        <SovosTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ width: 100, flexShrink: 0, textAlign: "right" }}
                            title="How strong this proposed link is (0–100%)"
                        >
                            Confidence
                        </SovosTypography>
                    </SovosBox>
                    <SovosBox sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {[...aiSuggestedColumns.entries()].map(([col, score], idx) => {
                            const targetField = mappings[col];
                            const field = mschFields.find((f) => f.id === targetField);
                            return (
                                <SovosBox
                                    key={col}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 0.75,
                                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <SovosTypography variant="caption" fontWeight={600} sx={{ width: 140, flexShrink: 0 }}>
                                        {stripCdmPrefix(col)}
                                    </SovosTypography>
                                    <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                                    <SovosTypography variant="caption" color="primary.main" sx={{ flex: 1 }}>
                                        {field?.name || stripCdmPrefix(targetField) || "—"}
                                    </SovosTypography>
                                    <SovosBox
                                        sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: "success.light", flexShrink: 0, width: 100, display: "flex", justifyContent: "flex-end" }}
                                        title="How strong this proposed link is (0–100%)"
                                    >
                                        <SovosTypography sx={{ fontSize: 10, color: "success.dark", fontWeight: 600 }}>
                                            {Math.round(score * 100)}%
                                        </SovosTypography>
                                    </SovosBox>
                                </SovosBox>
                            );
                        })}
                    </SovosBox>
                </SovosBox>
            )}

            <DataGrid
                key={`column-mapping-grid-${hideDoNotImport}`}
                id="column-mapping-grid"
                items={dgItems}
                header={dgHeader}
                card={rows.CellCard}
                status={{ loading: false }}
                metadata={{ total: dgItems.length }}
                enablePagination
                headerHeight={80}
                columnHeaderRenderer={columnHeaderRenderer}
                availableFilters={dgAvailableFilters}
                toolbar={MappingGridToolbar}
                toolbarProps={{ hideDoNotImport, onToggleHideDoNotImport, showDoNotImportToggle }}
                showCheckbox={false}
                modifierssx={{
                    "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                    "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                }}
            />

            <SovosDialog open={showAiMappingDialog} onClose={onCloseAiDialog} maxWidth="sm" fullWidth>
                <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                    <SovosIconButton size="small" tooltipText="Close" onClick={onCloseAiDialog}>
                        <Close fontSize="small" />
                    </SovosIconButton>
                </SovosBox>
                <SovosDialogTitle>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AutoAwesome sx={{ fontSize: 18, color: "primary.main" }} />
                        {"Column Mapping suggestions by Sovi"}
                    </SovosBox>
                </SovosDialogTitle>
                <SovosDialogContent>
                    {aiMappingResult && (
                        <SovosBox sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
                            <SovosTypography variant="caption" color="text.primary" sx={{ display: "block", fontWeight: 600 }}>
                                {`${aiMappingResult.column_mappings?.length ?? 0} proposed link${(aiMappingResult.column_mappings?.length ?? 0) !== 1 ? "s" : ""} in the table`}
                                {aiMappingResult.unmapped_source_columns?.length > 0
                                    ? ` · ${aiMappingResult.unmapped_source_columns.length} CSV column(s) with no proposed target`
                                    : ""}
                                {aiMappingResult.warnings?.length ? ` · ${aiMappingResult.warnings.length} notice(s)` : ""}
                            </SovosTypography>
                        </SovosBox>
                    )}
                    {aiMappingResult?.warnings?.length > 0 && (
                        <SovosBox sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: "warning.light", border: "1px solid", borderColor: "warning.light" }}>
                            {aiMappingResult.warnings.map((w) => (
                                <SovosTypography key={w} variant="caption" color="warning.dark" sx={{ display: "block" }}>{w}</SovosTypography>
                            ))}
                        </SovosBox>
                    )}
                    <SovosBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <SovosBox
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: "action.selected",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>CSV column</SovosTypography>
                            <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                            <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>Suggested target</SovosTypography>
                            <SovosTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ width: 100, flexShrink: 0, textAlign: "right" }}
                                title="How strong this proposed link is (0–100%)"
                            >
                                Confidence
                            </SovosTypography>
                        </SovosBox>
                        {(aiMappingResult?.column_mappings || []).length === 0 && (
                            <SovosBox sx={{ px: 1.5, py: 2 }}>
                                <SovosTypography variant="caption" color="text.secondary">
                                    No proposed column → field links in this run. Use the notices above, map in the grid, or try suggestions again.
                                </SovosTypography>
                            </SovosBox>
                        )}
                        {(aiMappingResult?.column_mappings || []).map(({ source_column, target_field, display_name, score, rationale }, idx) => {
                            const resolvedId = resolveAiTargetToGridFieldId(target_field, mschFields);
                            const field = mschFields.find((f) => f.id === resolvedId);
                            const targetLabel = field?.name || display_name || target_field;
                            const pct = Math.round((score ?? 0) * 100);
                            return (
                                <SovosBox
                                    key={`${source_column}-${idx}`}
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
                                        <SovosTypography variant="caption" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap title={source_column}>{source_column}</SovosTypography>
                                        <SovosTypography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>→</SovosTypography>
                                        <SovosTypography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap title={resolvedId}>{targetLabel}</SovosTypography>
                                    </SovosBox>
                                    <SovosBox sx={{ mb: rationale ? 0.75 : 0 }}>
                                        <SovosBox sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                                            <SovosTypography variant="caption" color="text.secondary">{"Confidence score"}</SovosTypography>
                                            <SovosTypography variant="caption" fontWeight={600} sx={{ color: "primary.main" }}>{`${pct}% confidence`}</SovosTypography>
                                        </SovosBox>
                                        <SovosBox sx={{ height: 4, borderRadius: 2, overflow: "hidden", bgcolor: "action.selected" }}>
                                            <SovosBox sx={{ width: `${pct}%`, height: "100%", background: AI_CONFIDENCE_GRADIENT }} />
                                        </SovosBox>
                                    </SovosBox>
                                    {rationale && (
                                        <SovosBox>
                                            <SovosTypography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block" }}>{"AI Reasoning"}</SovosTypography>
                                            <SovosTypography variant="caption" color="text.secondary">{rationale}</SovosTypography>
                                        </SovosBox>
                                    )}
                                </SovosBox>
                            );
                        })}
                    </SovosBox>
                    {aiMappingResult?.unmapped_source_columns?.length > 0 && (
                        <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            {`${aiMappingResult.unmapped_source_columns.length} column(s) could not be mapped: ${aiMappingResult.unmapped_source_columns.join(", ")}`}
                        </SovosTypography>
                    )}
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton variant="text" onClick={onCloseAiDialog}>Discard</SovosButton>
                    <SovosButton
                        variant="contained"
                        onClick={onApplyAiMappings}
                        disabled={!(aiMappingResult?.column_mappings?.length > 0)}
                    >
                        Apply to grid
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>
        </>
    );
};

export default StepMapColumns;
